<script setup lang="ts">
import { onClickOutside, useEventListener } from '@vueuse/core';

type ShareLevel = 'guest' | 'registered' | 'verified';

type ShareStats = {
  shares: number;
  leadsFromProject: number;
  pendingCommissionAed: number;
};

type ShareProperty = {
  title: { en: string };
  slug: string;
  developerSlug: string;
  galleryFirst?: string | null;
  fromAed: number;
  paymentPlan?: string | null;
  quarter?: string | null;
};

const {
  open,
  level,
  property,
  agentReference = null,
  agentInitials = null,
  brochureUrl = null,
  shareStats = null,
} = defineProps<{
  open: boolean;
  level: ShareLevel;
  property: ShareProperty;
  agentReference?: string | null;
  agentInitials?: string | null;
  brochureUrl?: string | null;
  shareStats?: ShareStats | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'share', payload: { channel: string }): void;
}>();

const { t } = useI18n();

const popoverRef = ref<HTMLElement | null>(null);
const copied = ref(false);
let copiedTimer: ReturnType<typeof setTimeout> | null = null;

const isVerified = computed(() => level === 'verified' && !!agentReference);

const runtimeConfig = useRuntimeConfig();
// Resolve from the runtime appUrl env so dev/staging/prod share the right host
// (NUXT_PUBLIC_SITE_URL). Falls back to the production canonical when missing.
const shareOrigin = computed(() => {
  const raw = (runtimeConfig.public.appUrl as string | undefined)?.trim();
  return (raw && raw.replace(/\/$/, '')) || 'https://insidersofdubai.com';
});
const shareOriginDisplay = computed(() => shareOrigin.value.replace(/^https?:\/\//, '').replace(/\/$/, ''));

const canonicalPath = computed(() =>
  property.developerSlug ? `/projects/${property.developerSlug}/${property.slug}` : `/p/${property.slug}`,
);

const shareUrl = computed(() => {
  const base = `${shareOrigin.value}${canonicalPath.value}`;
  return isVerified.value ? `${base}?ref=${agentReference}` : base;
});

const displayUrl = computed(() => {
  const base = `${shareOriginDisplay.value}${canonicalPath.value}`;
  return isVerified.value ? `${base}?ref=${agentReference}` : base;
});

const titleText = computed(() => property.title.en);

const previewSubLine = computed(() => {
  const parts: string[] = [t('property.sharePopover.fromAed', { amount: formatAed(property.fromAed) })];
  if (property.paymentPlan) parts.push(t('property.sharePopover.paymentPlanSuffix', { plan: property.paymentPlan }));
  if (property.quarter) parts.push(property.quarter);
  return parts.join(' · ');
});

function formatAed(value: number): string {
  if (!value || Number.isNaN(value)) return '—';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return `${value}`;
}

function formatStatCommission(value: number): string {
  if (!value) return 'AED 0';
  if (value >= 1_000_000) return `AED ${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (value >= 1_000) return `AED ${Math.round(value / 1_000)}k`;
  return `AED ${value}`;
}

const shareMessage = computed(() => `${titleText.value} — ${shareUrl.value}`);

type Channel = {
  key: string;
  label: string;
  sub?: string;
  accent?: 'wa';
  href: () => string | null;
  useNativeShare?: boolean;
};

const channels = computed<Channel[]>(() => {
  const base: Channel[] = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      sub: isVerified.value ? t('property.sharePopover.channels.toBuyer') : undefined,
      accent: 'wa',
      href: () => `https://wa.me/?text=${encodeURIComponent(shareMessage.value)}`,
    },
    {
      key: 'email',
      label: t('property.sharePopover.channels.email'),
      sub: isVerified.value ? t('property.sharePopover.channels.withBrochure') : undefined,
      href: () => {
        const body =
          brochureUrl && isVerified.value
            ? t('property.sharePopover.emailBodyWithBrochure', { message: shareMessage.value, url: brochureUrl })
            : shareMessage.value;
        return `mailto:?subject=${encodeURIComponent(titleText.value)}&body=${encodeURIComponent(body)}`;
      },
    },
  ];

  if (isVerified.value) {
    base.push({
      key: 'sms',
      label: 'SMS',
      href: () => `sms:?&body=${encodeURIComponent(shareMessage.value)}`,
    });
  } else {
    base.push({
      key: 'telegram',
      label: 'Telegram',
      href: () =>
        `https://t.me/share/url?url=${encodeURIComponent(shareUrl.value)}&text=${encodeURIComponent(titleText.value)}`,
    });
  }

  base.push(
    {
      key: 'x',
      label: 'X',
      href: () =>
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl.value)}&text=${encodeURIComponent(titleText.value)}`,
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      href: () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl.value)}`,
    },
    {
      key: 'more',
      label: t('property.sharePopover.channels.more'),
      href: () => null,
      useNativeShare: true,
    },
  );

  return base;
});

const channelIcons: Record<string, string> = {
  whatsapp:
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-1.7-.9-2.9-1.6-4-3.6-.3-.5.3-.5.8-1.6.1-.2 0-.3 0-.5-.1-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.7.4-.3.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2.1 3.2 5 4.5 1.9.8 2.6.9 3.5.7.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.5 15.2L2 22l4.9-1.3A10 10 0 1 0 12 2z"/></svg>',
  email:
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><polyline points="3 7 12 13 21 7"/></svg>',
  telegram:
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M22 4.5L18.6 20.6c-.3 1.2-1 1.5-2 .9l-5.5-4-2.7 2.6c-.3.3-.5.5-1 .5l.4-5.4 9.9-8.9c.4-.4-.1-.6-.7-.2L4.7 12.8 0 11.3c-1-.3-1-1 .2-1.5L20.7 2.4c.9-.3 1.7.2 1.3 2.1z"/></svg>',
  sms: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  x: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  linkedin:
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14a5 5 0 0 0-5 5v14a5 5 0 0 0 5 5h14a5 5 0 0 0 5-5V5a5 5 0 0 0-5-5zM8 19H5V8h3zm-1.5-12.3a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4zM20 19h-3v-5.6c0-3.4-4-3.1-4 0V19h-3V8h3v1.8c1.4-2.6 7-2.8 7 2.5z"/></svg>',
  more: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>',
};

async function handleChannelClick(channel: Channel) {
  if (channel.useNativeShare && typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ title: titleText.value, text: titleText.value, url: shareUrl.value });
      emit('share', { channel: channel.key });
      return;
    } catch {
      // User cancelled or share unavailable — fall through to copy
      await copyLink();
      return;
    }
  }
  const href = channel.href();
  if (href) {
    window.open(href, '_blank', 'noopener');
  }
  emit('share', { channel: channel.key });
}

async function copyLink() {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(shareUrl.value);
      copied.value = true;
      if (copiedTimer) clearTimeout(copiedTimer);
      copiedTimer = setTimeout(() => {
        copied.value = false;
      }, 1600);
      emit('share', { channel: 'copy' });
    }
  } catch {
    // Silent: clipboard write may fail in insecure contexts
  }
}

function handleBrochureDownload() {
  if (!brochureUrl) return;
  window.open(brochureUrl, '_blank', 'noopener');
  emit('share', { channel: 'brochure' });
}

onClickOutside(popoverRef, () => {
  if (open) emit('close');
});

useEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key === 'Escape' && open) emit('close');
});

onBeforeUnmount(() => {
  if (copiedTimer) clearTimeout(copiedTimer);
});
</script>

<template>
  <div
    v-if="open"
    ref="popoverRef"
    class="absolute end-0 top-[calc(100%+8px)] z-50 w-[380px] max-w-[calc(100vw-32px)] rounded-2xl border border-black-10 bg-white p-4 shadow-2xl"
    role="dialog"
    :aria-label="t('property.sharePopover.ariaLabel')"
  >
    <div
      class="absolute -top-1.5 end-6 size-3 rotate-45 border-s border-t border-black-10 bg-white"
      aria-hidden="true"
    />

    <div class="mb-3 flex items-start justify-between gap-3">
      <div class="min-w-0">
        <div
          class="text-[10px] font-semibold uppercase tracking-[0.12em]"
          :class="isVerified ? 'text-gold-primary-120' : 'text-black-60'"
        >
          {{ isVerified ? t('property.sharePopover.referralLinkTitle') : t('property.sharePopover.shareTitle') }}
        </div>
        <div class="mt-0.5 truncate text-base font-semibold text-black-100">
          {{ isVerified ? t('property.sharePopover.shareWithTag', { title: titleText }) : titleText }}
        </div>
      </div>
      <button
        type="button"
        class="-me-1 -mt-1 flex size-7 shrink-0 items-center justify-center rounded-full text-black-60 hover:bg-black-05 hover:text-black-100"
        :aria-label="t('common.actions.close')"
        @click="emit('close')"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line
            x1="6"
            y1="6"
            x2="18"
            y2="18"
          />
          <line
            x1="18"
            y1="6"
            x2="6"
            y2="18"
          />
        </svg>
      </button>
    </div>

    <div class="mb-3 flex items-stretch gap-3 rounded-xl border border-black-10 bg-black-03 p-2.5">
      <div class="size-14 shrink-0 overflow-hidden rounded-lg bg-gold-primary-20">
        <img
          v-if="property.galleryFirst"
          :src="property.galleryFirst"
          :alt="titleText"
          class="size-full object-cover"
        />
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-black-60">
          <span
            v-if="isVerified"
            class="rounded-sm bg-gold-primary-100 px-1.5 py-[1px] text-[9px] font-bold text-white"
          >
            {{ t('property.sharePopover.referralBadge') }}
          </span>
          {{ shareOriginDisplay }}
        </div>
        <div class="truncate text-sm font-semibold text-black-100">{{ titleText }}</div>
        <div class="truncate text-[11px] text-black-60">{{ previewSubLine }}</div>
        <div class="mt-0.5 truncate text-[11px] text-primary-100">{{ displayUrl }}</div>
      </div>
    </div>

    <div class="mb-3 flex items-center gap-2">
      <div
        class="flex min-w-0 flex-1 items-center gap-2 rounded-lg border bg-white px-2.5 py-2 text-xs"
        :class="isVerified ? 'border-additional-1-100/40' : 'border-black-10'"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="shrink-0 text-black-60"
        >
          <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
          <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
        </svg>
        <span class="truncate font-mono text-black-90">
          <template v-if="isVerified">
            {{ shareOriginDisplay }}{{ canonicalPath }}?ref=<b>{{ agentReference }}</b>
          </template>
          <template v-else>{{ displayUrl }}</template>
        </span>
        <span
          v-if="isVerified"
          class="ms-auto shrink-0 rounded-full bg-additional-1-100 px-1.5 py-[1px] text-[9px] font-bold text-white"
        >
          {{ t('property.sharePopover.you') }}
        </span>
      </div>
      <button
        type="button"
        class="flex shrink-0 items-center gap-1.5 rounded-lg border border-black-10 bg-white px-3 py-2 text-xs font-semibold text-black-100 transition hover:bg-black-05"
        @click="copyLink"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <rect
            x="9"
            y="9"
            width="11"
            height="11"
            rx="2"
          />
          <path d="M5 15V5a2 2 0 0 1 2-2h10" />
        </svg>
        {{ copied ? t('property.sharePopover.copied') : t('common.actions.copy') }}
      </button>
    </div>

    <button
      v-if="isVerified"
      type="button"
      class="mb-3 flex w-full items-center gap-3 rounded-xl border border-gold-primary-30 bg-gold-primary-10 p-2.5 text-start transition disabled:cursor-not-allowed disabled:opacity-60"
      :class="{ 'hover:bg-gold-primary-20': !!brochureUrl }"
      :disabled="!brochureUrl"
      @click="handleBrochureDownload"
    >
      <div class="relative size-10 shrink-0">
        <div
          class="flex size-10 items-center justify-center rounded-lg bg-additional-black-currant-100 text-xs font-bold text-white"
        >
          {{ agentInitials || t('property.sharePopover.you') }}
        </div>
        <div
          class="absolute -bottom-1 -end-1 flex size-5 items-center justify-center rounded-full border-2 border-gold-primary-10 bg-error-100 text-white"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
            <path d="M14 3v5h5" />
          </svg>
        </div>
      </div>
      <div class="min-w-0 flex-1">
        <div class="text-xs font-semibold text-black-100">{{ t('property.sharePopover.brochureTitle') }}</div>
        <div class="truncate text-[11px] text-black-60">
          {{
            brochureUrl ? t('property.sharePopover.brochureWithPhoto') : t('property.sharePopover.brochureNoBrochure')
          }}
        </div>
      </div>
      <div class="flex shrink-0 items-center gap-1 text-xs font-semibold text-gold-primary-120">
        <span>{{ t('property.sharePopover.download') }}</span>
        <span>↓</span>
      </div>
    </button>

    <div class="mb-3 grid grid-cols-3 gap-1.5">
      <button
        v-for="ch in channels"
        :key="ch.key"
        type="button"
        class="flex flex-col items-center justify-center gap-1 rounded-lg border border-black-10 bg-white p-2.5 text-center transition hover:bg-black-05"
        :class="
          ch.accent === 'wa'
            ? '!border-whatsapp-green/30 text-whatsapp-green hover:bg-whatsapp-green/5'
            : 'text-black-90'
        "
        @click="handleChannelClick(ch)"
      >
        <span
          class="flex size-5 items-center justify-center"
          v-html="channelIcons[ch.key]"
        />
        <span class="flex flex-col leading-tight">
          <span class="text-[11px] font-semibold text-black-100">{{ ch.label }}</span>
          <span
            v-if="ch.sub"
            class="text-[9px] uppercase tracking-wider text-black-60"
            >{{ ch.sub }}</span
          >
        </span>
      </button>
    </div>

    <div
      v-if="isVerified && shareStats"
      class="mb-3 flex items-stretch gap-1 rounded-xl border border-additional-1-100/30 bg-additional-1-10 p-2.5"
    >
      <div class="flex flex-1 flex-col items-center">
        <div class="text-base font-bold text-black-100">{{ shareStats.shares }}</div>
        <div class="text-[10px] text-black-60">{{ t('property.sharePopover.stats.shares') }}</div>
      </div>
      <div class="w-px bg-additional-1-100/30" />
      <div class="flex flex-1 flex-col items-center">
        <div class="text-base font-bold text-black-100">{{ shareStats.leadsFromProject }}</div>
        <div class="text-[10px] text-black-60">{{ t('property.sharePopover.stats.leadsFromProject') }}</div>
      </div>
      <div class="w-px bg-additional-1-100/30" />
      <div class="flex flex-1 flex-col items-center">
        <div class="text-base font-bold text-black-100">
          {{ formatStatCommission(shareStats.pendingCommissionAed) }}
        </div>
        <div class="text-[10px] text-black-60">{{ t('property.sharePopover.stats.pendingCommission') }}</div>
      </div>
    </div>

    <div
      v-if="!isVerified"
      class="flex items-start gap-2 rounded-lg bg-gold-primary-10 p-2.5 text-[11px] leading-snug text-black-90"
    >
      <span class="mt-0.5 text-gold-primary-120">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2l3 7 7 .5-5.5 4.5L18 21l-6-3.8L6 21l1.5-7L2 9.5 9 9z" />
        </svg>
      </span>
      <span>
        <i18n-t
          keypath="property.sharePopover.guestPitch"
          tag="span"
        >
          <template #question
            ><b>{{ t('property.sharePopover.guestQuestion') }}</b></template
          >
          <template #link
            ><b>{{ t('property.sharePopover.guestLinkPhrase') }}</b></template
          >
        </i18n-t>
        <NuxtLink
          to="/agents/register"
          class="ms-1 font-semibold text-primary-100 hover:underline"
        >
          {{ t('property.sharePopover.signUpFree') }}
          <span
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >→</span
          >
        </NuxtLink>
      </span>
    </div>

    <div
      v-else
      class="flex items-start gap-2 rounded-lg bg-additional-1-10 p-2.5 text-[11px] leading-snug text-black-90"
    >
      <span class="mt-0.5 text-additional-1-120">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M12 3l8 3v6c0 5-3.4 8.5-8 9.5C7.4 20.5 4 17 4 12V6l8-3z" />
          <path
            d="M9 12l2 2 4-4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
      <span>
        <i18n-t
          keypath="property.sharePopover.verifiedPitch"
          tag="span"
        >
          <template #days
            ><b>{{ t('property.sharePopover.thirtyDays') }}</b></template
          >
        </i18n-t>
      </span>
    </div>
  </div>
</template>
